<template>
  <section class="pitch-edit-page">
    <MainNavbar class="pitch-edit-nav" />
    <div class="pitch-edit-shell">
      <router-link to="/ipromiseafrica/facility/my-facility" class="back-to-dashboard">
        <i class="fa-solid fa-arrow-left"></i>
        Back to Dashboard
      </router-link>
      <div class="pitch-edit-hero">
        <h1>Edit your pitch</h1>
        <p>Update your listing. Photos cannot be changed here.</p>
      </div>

      <div v-if="pageLoading" class="pitch-edit-state">
        <i class="fa-solid fa-spinner fa-spin"></i>
        <p>Loading your pitch…</p>
      </div>

      <div v-else-if="loadError" class="pitch-edit-state pitch-edit-state--error">
        <i class="fa-solid fa-triangle-exclamation"></i>
        <p>{{ loadErrorMessage }}</p>
        <button type="button" class="btn-primary" @click="$router.push('/ipromiseafrica/facility/my-facility')">
          Back to dashboard
        </button>
      </div>

      <template v-else>
        <div class="registration-container">
          <div class="form-card visible">
            <div class="form-header">
              <h2>Pitch details</h2>
              <p>Save to apply changes. Existing images are kept.</p>
            </div>

            <div class="photos-note">
              <i class="fa-solid fa-images"></i>
              <span>Pitch photos are managed at registration only. Contact support if you need to replace them.</span>
            </div>

            <form @submit.prevent="submitUpdate" class="registration-form">
              <div class="form-section">
                <h3>Pitch information</h3>
                <div class="form-grid">
                  <div class="input-group">
                    <label for="editPitchTier">Pitch tier *</label>
                    <select id="editPitchTier" v-model="formData.pitch_tier" required :disabled="saving">
                      <option value="">Select pitch tier</option>
                      <option value="premium">Premium</option>
                      <option value="standard">Standard</option>
                      <option value="basic">Basic</option>
                    </select>
                  </div>
                  <div class="input-group">
                    <label for="editPitchName">Pitch name *</label>
                    <input
                      id="editPitchName"
                      v-model="formData.pitch_name"
                      type="text"
                      required
                      :disabled="saving"
                    />
                  </div>
                  <div class="input-group">
                    <label for="editPitchType">Facility type *</label>
                    <select id="editPitchType" v-model="formData.pitch_type" required :disabled="saving">
                      <option value="">Select facility type</option>
                      <option
                        v-for="opt in facilityTypeOptions"
                        :key="opt.value"
                        :value="opt.value"
                      >
                        {{ opt.label }}
                      </option>
                    </select>
                  </div>
                  <div class="input-group">
                    <label for="editHourly">Hourly rate (Ksh) *</label>
                    <input
                      id="editHourly"
                      v-model.number="formData.hourly_rate"
                      type="number"
                      required
                      min="1"
                      :disabled="saving"
                    />
                  </div>
                  <div class="input-group">
                    <label for="editCap">Maximum players</label>
                    <input
                      id="editCap"
                      v-model="formData.capacity"
                      type="number"
                      min="0"
                      :disabled="saving"
                    />
                  </div>
                  <div class="input-group full-width">
                    <label for="editDesc">Description *</label>
                    <textarea
                      id="editDesc"
                      v-model="formData.pitch_description"
                      required
                      rows="4"
                      maxlength="500"
                      :disabled="saving"
                    />
                  </div>
                </div>
              </div>

              <div class="form-section">
                <h3>Location</h3>
                <div class="form-grid">
                  <div class="input-group full-width">
                    <label for="editAddr">Full address *</label>
                    <input id="editAddr" v-model="formData.address" type="text" required :disabled="saving" />
                  </div>
                  <div class="input-group">
                    <label for="editCounty">County *</label>
                    <select
                      id="editCounty"
                      v-model="formData.county"
                      required
                      :disabled="saving"
                      @change="updateZoneOptions"
                    >
                      <option value="">Select county</option>
                      <option value="Nairobi">Nairobi</option>
                      <option value="Mombasa">Mombasa</option>
                      <option value="Kisumu">Kisumu</option>
                      <option value="Kiambu">Kiambu</option>
                    </select>
                  </div>
                  <div class="input-group">
                    <label for="editZone">Zone *</label>
                    <select id="editZone" v-model="formData.zone" required :disabled="saving || !formData.county">
                      <option value="">Select zone</option>
                      <option v-for="z in zoneOptions" :key="z" :value="z">{{ z }}</option>
                    </select>
                  </div>
                  <div class="input-group">
                    <label for="editCountry">Country</label>
                    <input id="editCountry" v-model="formData.country" type="text" readonly class="input-readonly" />
                  </div>
                  <div class="input-group full-width">
                    <label for="editPin">Pin location URL *</label>
                    <input id="editPin" v-model="formData.pin_location_url" type="url" required :disabled="saving" />
                  </div>
                </div>
              </div>

              <div class="form-section">
                <h3>Amenities</h3>
                <div class="amenities-grid">
                  <label v-for="a in amenities" :key="a.id" class="amenity-checkbox">
                    <input type="checkbox" :value="a.id" v-model="formData.amenities" :disabled="saving" />
                    <span class="checkmark"></span>
                    <i :class="a.icon"></i>
                    {{ a.name }}
                  </label>
                </div>
              </div>

              <div class="form-actions">
                <button type="submit" class="submit-btn" :disabled="saving">
                  <span class="submit-btn-inner">
                    <i v-if="!saving" class="fa-solid fa-floppy-disk"></i>
                    <i v-else class="fa-solid fa-spinner fa-spin"></i>
                    {{ saving ? "Saving…" : "Save changes" }}
                  </span>
                </button>
                <button type="button" class="cancel-btn" :disabled="saving" @click="goDashboard">Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </template>
    </div>

    <div class="edit-pitch-footer-host">
      <ThirdpartyFooter />
    </div>
  </section>
</template>

<script>
import MainNavbar from "@/components/main/headernfooter/header.vue";
import ThirdpartyFooter from "@/routes/thirdparty/Footer.vue";
import { buildApiUrl } from "@/config/api";
import {
  FACILITY_TYPE_OPTIONS,
  normalizeFacilityType,
} from "@/utils/facilityTypes";

const ALLOWED_TYPES = FACILITY_TYPE_OPTIONS.map((t) => t.value);

export default {
  name: "FacilityOwnerEditFacility",
  components: { MainNavbar, ThirdpartyFooter },
  data() {
    return {
      facilityTypeOptions: FACILITY_TYPE_OPTIONS,
      pageLoading: true,
      saving: false,
      loadError: false,
      loadErrorMessage: "",
      zoneOptions: [],
      formData: {
        pitch_tier: "",
        pitch_name: "",
        pitch_type: "",
        hourly_rate: "",
        capacity: "",
        pitch_description: "",
        address: "",
        county: "",
        zone: "",
        country: "Kenya",
        pin_location_url: "",
        amenities: [],
        is_available: true,
      },
      zonesByCounty: {
        Nairobi: [
          "Westlands",
          "Dagoretti North",
          "Dagoretti South",
          "Langata",
          "Kibra",
          "Roysambu",
          "Kasarani",
          "Ruaraka",
          "Embakasi South",
          "Embakasi North",
          "Embakasi Central",
          "Embakasi East",
          "Embakasi West",
          "Makadara",
          "Kamukunji",
          "Starehe",
        ],
        Mombasa: ["Changamwe", "Jomvu", "Kisauni", "Likoni", "Mvita", "Nyali"],
        Kisumu: [
          "Kisumu Central",
          "Kisumu East",
          "Kisumu West",
          "Seme",
          "Nyando",
          "Muhoroni",
          "Nyakach",
        ],
        Kiambu: [
          "Gatundu North",
          "Gatundu South",
          "Githunguri",
          "Juja",
          "Kabete",
          "Kiambaa",
          "Kiambu Town",
          "Kikuyu",
          "Lari",
          "Limuru",
          "Ruiru",
          "Thika Town",
        ],
      },
      amenities: [
        { id: "lighting", name: "Flood Lighting", icon: "fa-solid fa-lightbulb" },
        { id: "changing", name: "Changing Rooms", icon: "fa-solid fa-person-booth" },
        { id: "parking", name: "Parking Space", icon: "fa-solid fa-car" },
        { id: "showers", name: "Showers", icon: "fa-solid fa-shower" },
        { id: "equipment", name: "Equipment Rental", icon: "fa-solid fa-basketball" },
        { id: "cafe", name: "Cafe/Snacks", icon: "fa-solid fa-utensils" },
        { id: "wifi", name: "Free WiFi", icon: "fa-solid fa-wifi" },
        { id: "first_aid", name: "First Aid Kit", icon: "fa-solid fa-kit-medical" },
      ],
    };
  },
  mounted() {
    this.checkAuthAndLoad();
  },
  methods: {
    emitToast(title, message, type = "info") {
      if (typeof window === "undefined") return;
      window.dispatchEvent(
        new CustomEvent("show-toast", { detail: { title, message, type } })
      );
    },

    normalizeType(val) {
      const key = normalizeFacilityType(val);
      return ALLOWED_TYPES.includes(key) ? key : "multi-purpose";
    },

    normalizeTier(val) {
      const s = String(val || "").toLowerCase().trim();
      if (["premium", "standard", "basic"].includes(s)) return s;
      return "";
    },

    updateZoneOptions() {
      this.formData.zone = "";
      this.zoneOptions = this.formData.county
        ? this.zonesByCounty[this.formData.county] || []
        : [];
    },

    async checkAuthAndLoad() {
      const token = localStorage.getItem("access_token");
      if (!token) {
        this.emitToast("Sign in required", "Please log in to edit your pitch.", "warning");
        this.$router.push("/ipromiseafrica/sign-in");
        return;
      }
      await this.loadPitch();
    },

    async loadPitch() {
      this.pageLoading = true;
      this.loadError = false;
      try {
        const token = localStorage.getItem("access_token");
        const res = await fetch(buildApiUrl("/api/facilities/my-facility"), {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        });
        const text = await res.text();
        if (res.status === 404 || !text || text === "null") {
          this.loadError = true;
          this.loadErrorMessage = "No pitch found for this account.";
          return;
        }
        if (!res.ok) {
          this.loadError = true;
          this.loadErrorMessage = "Could not load pitch details.";
          return;
        }
        const data = JSON.parse(text);
        if (!data || !data.id) {
          this.loadError = true;
          this.loadErrorMessage = "No pitch registered.";
          return;
        }

        this.formData.pitch_tier = this.normalizeTier(data.pitch_tier);
        this.formData.pitch_name = data.pitch_name || "";
        this.formData.pitch_type = this.normalizeType(data.pitch_type);
        this.formData.hourly_rate = Number(data.hourly_rate) || "";
        this.formData.capacity =
          data.capacity != null && data.capacity !== "" ? data.capacity : "";
        this.formData.pitch_description = data.pitch_description || "";
        this.formData.address = data.address || "";
        this.formData.county = data.city || "";
        this.formData.country = data.country || "Kenya";
        this.formData.zone = data.zone || "";
        this.formData.pin_location_url = data.pin_location_url || "";
        this.formData.amenities = Array.isArray(data.amenities) ? [...data.amenities] : [];
        this.formData.is_available = data.is_available !== false;

        this.updateZoneOptions();
        if (this.formData.zone && !this.zoneOptions.includes(this.formData.zone)) {
          this.zoneOptions = [...this.zoneOptions, this.formData.zone];
        }
      } catch (e) {
        console.error(e);
        this.loadError = true;
        this.loadErrorMessage = "Network error while loading pitch.";
      } finally {
        this.pageLoading = false;
      }
    },

    validateForm() {
      const req = [
        "pitch_tier",
        "pitch_name",
        "pitch_type",
        "pitch_description",
        "address",
        "county",
        "zone",
        "pin_location_url",
      ];
      for (const f of req) {
        if (!String(this.formData[f] ?? "").trim()) {
          this.emitToast("Incomplete", `Please fill in ${f.replace(/_/g, " ")}.`, "warning");
          return false;
        }
      }
      const hr = Number(this.formData.hourly_rate);
      if (!Number.isFinite(hr) || hr <= 0) {
        this.emitToast("Hourly rate", "Enter a valid hourly rate (KES).", "warning");
        return false;
      }
      const desc = String(this.formData.pitch_description || "").trim();
      if (desc.length < 20) {
        this.emitToast("Description", "Description must be at least 20 characters.", "warning");
        return false;
      }
      try {
        const u = new URL(
          String(this.formData.pin_location_url).trim().startsWith("http")
            ? this.formData.pin_location_url
            : `https://${this.formData.pin_location_url}`
        );
        if (!u.hostname) throw new Error("bad");
      } catch {
        this.emitToast("Pin location", "Enter a valid maps URL.", "warning");
        return false;
      }
      return true;
    },

    async submitUpdate() {
      if (!this.validateForm()) return;
      const token = localStorage.getItem("access_token");
      if (!token) {
        this.emitToast("Session", "Please sign in again.", "warning");
        this.$router.push("/ipromiseafrica/sign-in");
        return;
      }

      this.saving = true;
      try {
        let cap = this.formData.capacity;
        if (cap === "" || cap === null) cap = null;
        else cap = parseInt(cap, 10);

        const body = {
          pitch_tier: String(this.formData.pitch_tier).trim(),
          pitch_name: String(this.formData.pitch_name).trim(),
          pitch_type: this.formData.pitch_type,
          hourly_rate: Number(this.formData.hourly_rate),
          capacity: Number.isFinite(cap) ? cap : null,
          pitch_description: String(this.formData.pitch_description).trim(),
          address: String(this.formData.address).trim(),
          county: String(this.formData.county).trim(),
          country: String(this.formData.country || "Kenya").trim(),
          zone: String(this.formData.zone).trim(),
          pin_location_url: String(this.formData.pin_location_url).trim(),
          amenities: Array.isArray(this.formData.amenities) ? this.formData.amenities : [],
          is_available: !!this.formData.is_available,
        };

        const res = await fetch(buildApiUrl("/api/facilities/my-facility"), {
          method: "PATCH",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(body),
        });

        if (res.status === 401 || res.status === 403) {
          localStorage.removeItem("access_token");
          this.emitToast("Session expired", "Please sign in again.", "error");
          this.$router.push("/ipromiseafrica/sign-in");
          return;
        }

        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const msg = this.parseErr(err);
          throw new Error(msg);
        }

        this.emitToast("Saved", "Your pitch was updated.", "success");
        this.$router.push("/ipromiseafrica/facility/my-facility");
      } catch (e) {
        this.emitToast("Update failed", (e && e.message) || "Try again.", "error");
      } finally {
        this.saving = false;
      }
    },

    parseErr(err) {
      if (!err) return "Request failed";
      if (typeof err.detail === "string") return err.detail;
      if (Array.isArray(err.detail) && err.detail[0]?.msg) return err.detail[0].msg;
      return "Request failed";
    },

    goDashboard() {
      this.$router.push("/ipromiseafrica/facility/my-facility");
    },
  },
};
</script>

<style scoped>
.back-to-dashboard {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  color: var(--text-color1);
  text-decoration: none;
  font-size: 0.95rem;
  margin-bottom: 1rem;
  padding: 0.5rem 0;
  align-self: flex-start;
}

.back-to-dashboard:hover {
  color: var(--border-color-hover);
}

.pitch-edit-page {
  width: 100%;
  min-height: 100vh;
  background: var(--background-color1);
  font-family: "Poppins", sans-serif;
  overflow-x: hidden;
  display: flex;
  flex-direction: column;
}

.pitch-edit-nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
}

.pitch-edit-shell {
  max-width: 720px;
  margin: 0 auto;
  padding: 88px 20px 48px;
  width: 100%;
  box-sizing: border-box;
  flex: 1 0 auto;
  display: flex;
  flex-direction: column;
}

.pitch-edit-hero {
  text-align: center;
  margin-bottom: 28px;
  color: var(--text-color1);
}

.pitch-edit-hero h1 {
  font-size: clamp(1.65rem, 4vw, 2.1rem);
  margin: 0 0 8px;
  font-weight: 700;
}

.pitch-edit-hero p {
  margin: 0;
  color: var(--text-color2);
  font-size: 0.95rem;
}

.pitch-edit-state {
  text-align: center;
  padding: 48px 20px;
  color: var(--text-color2);
}

.pitch-edit-state--error {
  color: var(--text-color1);
}

.pitch-edit-state .btn-primary {
  margin-top: 16px;
  padding: 10px 20px;
  border-radius: 10px;
  border: none;
  cursor: pointer;
  font-weight: 600;
  background: var(--border-color-hover);
  color: var(--text-color3, #0a0a0a);
}

.registration-container {
  max-width: 100%;
  margin: 0 auto 32px;
  padding: 0;
}

.form-card {
  background: var(--background-color2);
  border-radius: 20px;
  box-shadow: 0 15px 50px rgba(0, 0, 0, 0.15);
  padding: clamp(24px, 4vw, 40px);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.photos-note {
  display: flex;
  gap: 12px;
  align-items: flex-start;
  padding: 12px 14px;
  border-radius: 12px;
  background: rgba(0, 255, 255, 0.08);
  border: 1px solid var(--border-color-hover1, rgba(0, 255, 255, 0.25));
  color: var(--text-color2);
  font-size: 0.88rem;
  margin-bottom: 24px;
}

.photos-note i {
  color: var(--border-color-hover);
  margin-top: 2px;
}

.form-header {
  text-align: center;
  margin-bottom: 20px;
}

.form-header h2 {
  color: var(--text-color1);
  font-size: 1.5rem;
  margin: 0 0 6px;
}

.form-header p {
  color: var(--text-color2);
  font-size: 0.9rem;
  margin: 0;
}

.form-section {
  margin-bottom: 28px;
  padding-bottom: 24px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}

.form-section:last-of-type {
  border-bottom: none;
}

.form-section h3 {
  color: var(--text-color1);
  font-size: 1.1rem;
  margin: 0 0 16px;
}

.form-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
}

.input-group.full-width {
  grid-column: 1 / -1;
}

.input-group label {
  display: block;
  margin-bottom: 6px;
  font-size: 0.85rem;
  font-weight: 600;
  color: var(--text-color2);
}

.input-group input,
.input-group select,
.input-group textarea {
  width: 100%;
  padding: 12px 14px;
  border-radius: 10px;
  border: 1px solid var(--border-color-hover) !important;
  background: var(--background-color2);
  color: var(--text-color1) !important;
  font-size: 0.95rem;
  box-sizing: border-box;
}

.input-readonly {
  opacity: 0.85;
  cursor: default;
}

.amenities-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  gap: 12px;
}

.amenity-checkbox {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px;
  border-radius: 10px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  cursor: pointer;
  color: var(--text-color1);
  font-size: 0.88rem;
}

.form-actions {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  justify-content: center;
  margin-top: 8px;
}

.submit-btn {
  padding: 12px 28px;
  border-radius: 12px;
  border: none;
  font-weight: 700;
  cursor: pointer;
  background: var(--border-color-hover);
  color: var(--text-color3, #0a0a0a);
}

.submit-btn:disabled {
  opacity: 0.65;
  cursor: not-allowed;
}

.cancel-btn {
  padding: 12px 24px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  background: transparent;
  color: var(--text-color1);
  cursor: pointer;
  font-weight: 600;
}

.edit-pitch-footer-host {
  margin: 0;
  padding: 0;
  width: 100%;
  flex-shrink: 0;
}

.edit-pitch-footer-host :deep(.thirdparty-footer) {
  margin-top: 0;
}

.submit-btn-inner {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
</style>
